import type { TFunction } from 'i18next';
// Import Types
import type { ParseToChartData } from './types';
// Import Services
import { customMomentWithoutTimezoneConversion } from 'services/Helpers';
import { getEndDate, getStartDate } from 'services/dashboard';
// Import Constants
import { FilterComparisons, l, DEFAULT_SORTING } from 'constants/common';
// Import Enums
import { BarColorEnum } from './enums';

const { In } = FilterComparisons;

export const parseChartData: ParseToChartData = (response, t) => {
    const categories: string[] = [];
    const deliveryCount: (number | null)[] = [];
    const openCount: (number | null)[] = [];
    const clickCount: (number | null)[] = [];

    response.forEach((element) => {
        deliveryCount.push(element.DeliveryCount === 0 ? null : element.DeliveryCount);
        openCount.push(element.OpenCount === 0 ? null : element.OpenCount);
        clickCount.push(element.ClickCount === 0 ? null : element.ClickCount);
        categories.push(customMomentWithoutTimezoneConversion(element['Date']).format('MMM DD'));
    });

    return {
        series: [
            {
                name: t(l.Delivered),
                data: deliveryCount,
                color: BarColorEnum.Delivered,
            },
            {
                name: t(l.TotalOpens),
                data: openCount,
                color: BarColorEnum.Open,
            },
            {
                name: t(l.TotalClicks),
                data: clickCount,
                color: BarColorEnum.Click,
            },
        ],
        categories: categories,
    };
};

export const getDate = (date) => [getStartDate(date[0]), getEndDate(date[1])];

export const getPromotionRequestBody = (channels: string[]) => {
    return {
        Filters: [{ Name: 'DeliveryMethod', Comparision: In, Values: channels }],
        Pageing: { PageSize: 5000, PageNumber: 1 },
        Sorting: DEFAULT_SORTING,
    };
};

export const getTemplateRequestBody = (channels: string[], promotions: string[]) => {
    return {
        Filters: [
            { Name: 'PromotionId', Comparision: In, Values: promotions },
            { Name: 'DeliveryMethod', Comparision: In, Values: channels },
        ],
        Pageing: { PageSize: 5000, PageNumber: 1 },
        Sorting: DEFAULT_SORTING,
    };
};

const formatRate = (numerator: number, denominator: number): string => {
    if (!denominator || !numerator) {
        return '0';
    }

    const rate = (numerator / denominator) * 100;

    if (rate < 0.1) {
        return '<0.1';
    }

    return rate.toFixed(1);
};

const createTooltipCountLine = (label: string, value: number, color: string): string => {
    return `<span style="display: inline-block; padding: 4px 0;"><span style="color:${color}">\u25CF</span> ${label}: <b>${value}</b></span><br/>`;
};

const createTooltipRateLine = (label: string, value: string): string => {
    return `<span style="display: inline-block; padding: 4px 0;"><b>${label}:</b> ${value}%</span><br/>`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const chartFormatter = (bar: any, t: TFunction) => {
    const delivered = bar.points?.[0]?.y || 0;
    const totalOpens = bar.points?.[1]?.y || 0;
    const totalClicks = bar.points?.[2]?.y || 0;

    const openRate = formatRate(totalOpens, delivered);
    const clickRate = formatRate(totalClicks, delivered);

    const tooltipText = `
        <b>${bar.x}</b><br/>
        ${createTooltipCountLine(t(l.Delivered), delivered, BarColorEnum.Delivered)}
        ${createTooltipCountLine(t(l.TotalOpens), totalOpens, BarColorEnum.Open)}
        ${createTooltipCountLine(t(l.TotalClicks), totalClicks, BarColorEnum.Click)}

        ${createTooltipRateLine(t(l.OpenRate), openRate)}
        ${createTooltipRateLine(t(l.ClickRate), clickRate)}
    `;

    return tooltipText;
};
